import React, { useEffect, useRef, useState } from "react";
import "./App.css";
import { blankState, saveStateToDb, State } from "./components/state";
import MenuBar from "./components/TopMenuBar";
import { Modals } from "./components/modals";
import MealSelection from "./components/MealSelction";
import MealDisplay from "./components/MealDisplay";
import ShoppingList from "./components/ShoppingList";
import { loadRecipes, loadState } from "./api";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { darkTheme, lightTheme } from "./theme";
import SnackbarAlert from "./components/Snackbar";
import { objectsAreEqual } from "./utils";
import LoadingBackdrop from "./components/LoadingBackdrop";

export const localStorageEmailKey = "email";
export const localStorageRecipeKey = "recipes";

function App() {
  const [state, setState] = useState(blankState);
  const [lastState, setLastState] = useState<State | undefined>(undefined);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);
  const [copySnackbarText, setCopySnackbarText] = useState("");
  const [saveRecipeSnackbarOpen, setSaveRecipeSnackbarOpen] = useState(false);
  const [saveRecipeSnackbarText, setSaveRecipeSnackbarText] = useState("");
  const [conditionalCheckFailCount, setConditionalCheckFailCount] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const lastRefresh = useRef(0);

  const baseUrl = window.location.origin;
  const tokenKey = `access_token`;
  const removeThis = `${baseUrl}/#`;
  const searchParams = new URLSearchParams(
    decodeURIComponent(window.location.href).replace(removeThis, ""),
  );
  if (searchParams.has(tokenKey)) {
    localStorage.setItem("signInToken", searchParams.get(tokenKey) ?? "");
    localStorage.setItem("state", searchParams.get("state") ?? "");
    window.opener = null;
    window.open("", "_self");
    window.close();
    // see LoginButton
  }

  useEffect(() => {
    const fetchData = async () => {
      const email = localStorage.getItem(localStorageEmailKey);
      setRefreshing(true);
      const loadedState = email
        ? await loadState({ email })
        : JSON.stringify(blankState);
      let userState;
      if (loadedState && email) {
        userState = JSON.parse(loadedState) as State;
        const sharedStateName = userState.settings?.sharedStateName;
        if (sharedStateName && sharedStateName !== "") {
          const sharedLoadedState = await loadState({ email, sharedStateName });
          if (sharedLoadedState) {
            userState = JSON.parse(sharedLoadedState);
          }
        }
      }

      let recipes = JSON.parse(
        localStorage.getItem(localStorageRecipeKey) ?? "{}",
      );
      if (!recipes) {
        recipes = await loadRecipes();
        localStorage.setItem(localStorageRecipeKey, JSON.stringify(recipes));
      }

      const newState = {
        ...blankState,
        ...userState,
      };
      if (!objectsAreEqual(newState, blankState)) {
        setState(newState);
      }
      setRefreshing(false);
    };

    fetchData();
  }, []);

  useEffect(() => {
    saveStateToDb({
      conditionalCheckFailCount,
      lastState,
      localStorageEmailKey,
      setConditionalCheckFailCount,
      setLastState,
      setSnackbarOpen,
      setState,
      state,
    });
  }, [state]);

  const refreshState = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const email = localStorage.getItem(localStorageEmailKey);
      const sharedStateName = state.settings?.sharedStateName;
      const stateKey =
        sharedStateName && sharedStateName !== "" ? sharedStateName : email;

      if (stateKey) {
        const loadedState = await loadState({ email: stateKey });
        if (loadedState) {
          const userState = JSON.parse(loadedState) as State;
          setState(userState);
        }
      }
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    const maybeRefresh = () => {
      const now = Date.now();

      // Ignore duplicate events within 500ms
      if (now - lastRefresh.current < 500) {
        return;
      }

      lastRefresh.current = now;
      void refreshState();
    };

    const handleFocus = () => maybeRefresh();

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        maybeRefresh();
      }
    };

    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        maybeRefresh();
      }
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pageshow", handlePageShow);

    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, [refreshState]);

  return (
    <ThemeProvider theme={state.darkMode ? darkTheme : lightTheme}>
      <CssBaseline />
      {MenuBar({ state, setState, setRefreshing })}
      {Modals({
        setState,
        state,
        setSaveRecipeSnackbarOpen,
        setSaveRecipeSnackbarText,
      })}
      {MealSelection({ state, setState })}
      {MealDisplay({ state, setState })}
      {ShoppingList({
        state,
        setState,
        setCopySnackbarOpen,
        setCopySnackbarText,
      })}
      {SnackbarAlert({
        message: copySnackbarText,
        open: copySnackbarOpen,
        setOpen: setCopySnackbarOpen,
        severity: copySnackbarText.toLowerCase().includes("fail")
          ? "error"
          : "success",
        autoHideDuration: 3000,
      })}
      {SnackbarAlert({
        message: "Could not save shopping list:",
        open: snackbarOpen,
        setOpen: setSnackbarOpen,
        severity: "error",
        autoHideDuration: 3000,
        action: (
          <>
            <span
              onClick={() => {
                saveStateToDb({
                  conditionalCheckFailCount,
                  lastState,
                  localStorageEmailKey,
                  setConditionalCheckFailCount,
                  setLastState,
                  setSnackbarOpen,
                  setState,
                  state,
                  forceSave: true,
                });
              }}
            >
              override
            </span>{" "}
            or{" "}
            <span
              onClick={() => {
                window.location.reload();
              }}
            >
              refresh
            </span>
          </>
        ),
      })}
      {SnackbarAlert({
        message: saveRecipeSnackbarText,
        open: saveRecipeSnackbarOpen,
        setOpen: setSaveRecipeSnackbarOpen,
        severity: saveRecipeSnackbarText.toLowerCase().includes("fail")
          ? "error"
          : "success",
        autoHideDuration: 3000,
      })}
      {LoadingBackdrop({ refreshing })}
    </ThemeProvider>
  );
}

export default App;
